/**
 * ============================================================================
 * VINPOP — Collection Recommendations
 * ============================================================================
 *
 * Fetches personalized wine recommendations from the VinPOP API and merges
 * them with the Shopify product catalog. Handles:
 *   - API calls to api.vinpop.fr with customer taste profile
 *   - Score/distance calculation and product matching
 *   - Catalog enrichment with recommendation data
 *   - Fallback to catalog-only mode when API is unavailable
 *
 * Loaded from: layout/theme.liquid (global, deferred)
 * Depends on: vinpop-translations.js, wine-catalog-data (Liquid snippet)
 * ============================================================================
 */

if (!window.collectionRecommendationsLoaded) {
  window.collectionRecommendationsLoaded = true;

  // GLOBAL EVENT DELEGATION - Intercept ALL product link clicks on wine-assistant
  // This runs immediately, before DOMContentLoaded, using capture phase
  (function setupGlobalClickInterceptor() {
    const pathname = window.location.pathname;
    const isWineAssistant = pathname.includes('/wine-assistant');
    
    if (!isWineAssistant) {
      // console.log('⏭️ Not on wine-assistant, skipping global interceptor');
      return;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const hasSourceProduct = urlParams.get('source_product') || urlParams.get('main-product');
    
    if (hasSourceProduct) {
      // console.log('⏭️ Already in comparison mode (source_product present), skipping interceptor');
      return;
    }
    
    // console.log('🎯 Setting up GLOBAL click interceptor for wine-assistant');
    
    // Use capture phase at document level - this WILL catch all clicks before anything else
    document.addEventListener('click', function(e) {
      // Find if click is on or inside a product link
      const productLink = e.target.closest('a[href*="/products/"]');
      
      if (!productLink) return; // Not a product link click
      
      // Double-check we're still on wine-assistant (in case of SPA navigation)
      if (!window.location.pathname.includes('/wine-assistant')) return;
      
      // Check if already in comparison mode
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.get('source_product') || currentParams.get('main-product')) return;
      
      // console.log('🎯 INTERCEPTED product link click:', productLink.href);
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Find product card and ID
      const productCard = productLink.closest('li[data-product-id], .card, .product-tile');
      let productId = productCard ? productCard.getAttribute('data-product-id') : null;
      
      if (!productId) {
        // Extract from URL
        const urlParts = productLink.href.split('/');
        const productIndex = urlParts.indexOf('products');
        if (productIndex !== -1 && urlParts[productIndex + 1]) {
          // Remove query string from handle
          productId = urlParts[productIndex + 1].split('?')[0];
        }
      }
      
      if (productId) {
        // console.log('✅ Product ID found:', productId);
        
        // Store product info for later use
        if (productCard) {
          const titleElement = productCard.querySelector('h3, h2, .product-title, [class*="title"]');
          const productTitle = titleElement ? titleElement.textContent.trim() : '';
          sessionStorage.setItem('wineAssistantClickedProduct', productId);
          sessionStorage.setItem('wineAssistantClickedProductName', productTitle);
          sessionStorage.setItem('wineAssistantClickedTime', Date.now().toString());
        }
        
        // DO NOT preserve filters - they would limit the recommendation results
        // The recommendation system needs access to ALL products in the collection
        // Build redirect URL with ONLY source_product parameter
        const currentCollection = pathname.match(/\/collections\/([^\/\?]+)/)?.[1] || 'wine-assistant';
        const languagePrefix = pathname.match(/^\/(en|nl|fr|de|es|it|pt|da)/)?.[0] || '';
        const redirectUrl = `${languagePrefix}/collections/${currentCollection}?source_product=${productId}`;
        
        // console.log('🚀 Redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
      } else {
        console.warn('❌ No product ID found, allowing normal navigation');
        // Don't prevent default - let normal navigation happen
      }
    }, true); // TRUE = capture phase, runs BEFORE bubbling phase handlers
    
    // console.log('✅ Global click interceptor installed');
  })();

  // Translation helper function
function getTranslation(key) {
  const translations = window.theme && window.theme.translations ? window.theme.translations : {};
  const translationKey = 'wine_recommendations_' + key;
  return translations[translationKey] || key;
}

  async function waitForWineManager() {
    if (window.wineManager) return window.wineManager;

    for (let attempt = 0; attempt < 20; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      if (window.wineManager) return window.wineManager;
    }

    return null;
  }

  // Function to attach wine assistant navigation
  function attachWineAssistantNavigation() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasSourceProduct = urlParams.get('source_product') || urlParams.get('main-product');
    const pathname = window.location.pathname;

    // Check for special collections (with or without language prefix like /en, /nl, /fr, etc.)
    const isWineAssistant = pathname.includes('/wine-assistant');
    
    // Skip navigation only on wine-assistant when in comparison mode
    if (isWineAssistant && hasSourceProduct) {
      return;
    }
    
    // Only intercept navigation on wine-assistant collection
    if (!isWineAssistant) {
      return;
    }

    // console.log('🍷 Attaching Wine Assistant Navigation on:', pathname);

    // Find all product links (including newly filtered ones)
    const productLinks = document.querySelectorAll('a[href*="/products/"]:not([data-wine-assistant-attached])');
    // console.log('📦 Found product links to attach:', productLinks.length);

    productLinks.forEach((link, index) => {
      const productCard = link.closest('li[data-product-id], .card');
      if (productCard) {
        link.dataset.wineAssistantAttached = 'true'; // Mark as attached to prevent duplicates
        
        link.addEventListener('click', (e) => {
            // console.log('🎯 Product link clicked! Redirecting to wine-assistant with source_product...');
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Get product ID from card or URL
            let productId = productCard.getAttribute('data-product-id') || productCard.getAttribute('data-product-id');

            if (!productId) {
              // Extract from URL
              const urlParts = link.href.split('/');
              const productIndex = urlParts.indexOf('products');
              if (productIndex !== -1 && urlParts[productIndex + 1]) {
                productId = urlParts[productIndex + 1]; // Use handle as fallback
              }
            }

            if (productId) {
              // console.log('✅ Redirecting with product ID:', productId);

              // Get product name and store for later use
              const titleElement = productCard.querySelector('h3, h2, .product-title, [class*="title"]');
              const productTitle = titleElement ? titleElement.textContent.trim() : '';

              sessionStorage.setItem('wineAssistantClickedProduct', productId);
              sessionStorage.setItem('wineAssistantClickedProductName', productTitle);
              sessionStorage.setItem('wineAssistantClickedTime', Date.now().toString());

              // console.log('📦 Stored product name:', productTitle);

              // Preserve existing filters from current URL
              const currentUrl = new URL(window.location.href);
              const existingParams = new URLSearchParams(currentUrl.search);
              
              // Remove parameters that shouldn't be preserved
              existingParams.delete('page');
              existingParams.delete('sort_by');
              existingParams.delete('source_product');
              existingParams.delete('main-product');
              
              // Add source_product parameter
              existingParams.set('source_product', encodeURIComponent(productId));

              // Redirect to same collection with source product and preserved filters
              const currentCollection = pathname.match(/\/collections\/([^\/\?]+)/)?.[1] || 'wine-assistant';
              const languagePrefix = pathname.match(/^\/(en|nl|fr|de|es|it|pt|da)/)?.[0] || '';
              const queryString = existingParams.toString();
              window.location.href = `${languagePrefix}/collections/${currentCollection}${queryString ? '?' + queryString : ''}`;
            } else {
              console.warn('❌ No product ID found, normal navigation');
              window.location.href = link.href;
            }
          }, true); // Use capture phase to intercept before other handlers
        }
      });
  }

  // ========== CARD ALIGNMENT FUNCTION ==========
  // Equalizes heights of product card titles and recommendation text for consistent alignment
  // Called AFTER recommendation badges are added (not on initial page load to avoid visible jumps)
  function alignProductCards() {
    const grids = document.querySelectorAll('ul.grid.gap-theme, #product-grid, #facets-results ul.grid');
    
    grids.forEach(grid => {
      const cards = grid.querySelectorAll('li[data-product-id]');
      if (cards.length === 0) return;
      
      // Get number of columns from computed style
      const gridStyle = window.getComputedStyle(grid);
      const gridTemplateColumns = gridStyle.getPropertyValue('grid-template-columns');
      const numColumns = gridTemplateColumns.split(' ').length || 3;
      
      // Process cards in rows
      const cardsArray = Array.from(cards);
      for (let i = 0; i < cardsArray.length; i += numColumns) {
        const rowCards = cardsArray.slice(i, i + numColumns);
        
        // Reset heights first
        rowCards.forEach(card => {
          const h3 = card.querySelector('h3.font-body, h3');
          if (h3) h3.style.height = 'auto';
          const recText = card.querySelector('.wine-recommendation-text');
          if (recText) recText.style.height = 'auto';
        });
        
        // Find max h3 height in this row
        let maxTitleHeight = 0;
        rowCards.forEach(card => {
          const h3 = card.querySelector('h3.font-body, h3');
          if (h3) maxTitleHeight = Math.max(maxTitleHeight, h3.offsetHeight);
        });
        
        // Find max recommendation text height in this row
        let maxRecHeight = 0;
        rowCards.forEach(card => {
          const recText = card.querySelector('.wine-recommendation-text');
          if (recText) maxRecHeight = Math.max(maxRecHeight, recText.offsetHeight);
        });
        
        // Apply max heights to all cards in row
        rowCards.forEach(card => {
          const h3 = card.querySelector('h3.font-body, h3');
          if (h3 && maxTitleHeight > 0) {
            h3.style.height = maxTitleHeight + 'px';
            h3.style.overflow = 'hidden';
          }
          const recText = card.querySelector('.wine-recommendation-text');
          if (recText && maxRecHeight > 0) {
            recText.style.height = maxRecHeight + 'px';
          }
        });
      }
    });
  }

  function isWineAssistantCollectionPage(pathname) {
    return /\/collections\/wine-assistant\/?$/i.test(pathname || '');
  }

  function normalizeFacetValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  const countryAliases = {
    france: ['frankrijk'],
    frankrijk: ['france'],
    germany: ['duitsland'],
    duitsland: ['germany'],
    italy: ['italie'],
    italie: ['italy'],
    georgia: ['georgie'],
    georgie: ['georgia'],
    spain: ['spanje'],
    spanje: ['spain'],
    'south africa': ['zuid-afrika', 'zuid afrika'],
    'zuid-afrika': ['south africa', 'zuid afrika'],
    'zuid afrika': ['south africa', 'zuid-afrika'],
    chile: ['chili'],
    chili: ['chile'],
    armenia: ['armenie'],
    armenie: ['armenia'],
    azerbaijan: ['azerbeidzjan'],
    azerbeidzjan: ['azerbaijan'],
    'new zealand': ['nieuw-zeeland', 'nieuw zeeland'],
    'nieuw-zeeland': ['new zealand', 'nieuw zeeland'],
    'nieuw zeeland': ['new zealand', 'nieuw-zeeland'],
    us: ['vs'],
    vs: ['us']
  };

  function expandCountryAliases(values) {
    const expanded = new Set();
    (values || []).forEach((value) => {
      const normalized = normalizeFacetValue(value);
      if (!normalized) return;
      expanded.add(normalized);
      const aliases = countryAliases[normalized] || [];
      aliases.forEach((alias) => {
        expanded.add(normalizeFacetValue(alias));
      });
    });
    return Array.from(expanded);
  }

  function splitCountryValues(rawValue) {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    }

    return String(rawValue || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function splitGenericValues(rawValue) {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    }

    return String(rawValue || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function getCountryValuesForCard(card) {
    const productId = card.getAttribute('data-product-id');
    const catalogEntry = productId && window.shopCatalog ? window.shopCatalog[productId] : null;
    const cardCountry = card.getAttribute('data-country-origin') || card.dataset.countryOrigin || '';
    const rawCountry = cardCountry || (catalogEntry && catalogEntry.country) || '';
    return splitCountryValues(rawCountry).map(normalizeFacetValue);
  }

  function getFacetValuesFromCatalogEntry(entry, paramName) {
    if (!entry) return [];
    const key = String(paramName || '').toLowerCase();

    if (key.includes('country_of_origin')) {
      return splitCountryValues(entry.country).map(normalizeFacetValue);
    }

    if (key.includes('region_of_origin') || key.includes('region')) {
      return splitGenericValues(entry.region).map(normalizeFacetValue);
    }

    if (key.includes('grape_variety')) {
      return splitGenericValues(entry.grapeVariety).map(normalizeFacetValue);
    }

    if (key.includes('product_type') || key.endsWith('.type')) {
      return splitGenericValues(entry.type).map(normalizeFacetValue);
    }

    if (key.includes('wine_color')) {
      return splitGenericValues(entry.wineColor || entry.type).map(normalizeFacetValue);
    }

    if (key.includes('food_pairing')) {
      return splitGenericValues(entry.foodPairing).map(normalizeFacetValue);
    }

    if (key.includes('body')) {
      return splitGenericValues(entry.body).map(normalizeFacetValue);
    }

    if (key.includes('vendor')) {
      return splitGenericValues(entry.vendor).map(normalizeFacetValue);
    }

    return [];
  }

  function collectWineAssistantProductsForFacets() {
    const cards = Array.from(document.querySelectorAll('#facets-results li[data-product-id]'));
    return cards.map((card) => {
      const productId = String(card.getAttribute('data-product-id') || '').trim();
      if (!productId) return null;
      const entry = window.shopCatalog && window.shopCatalog[productId] ? window.shopCatalog[productId] : null;
      if (!entry) return null;
      return { productId, entry };
    }).filter(Boolean);
  }

  function getSelectedFacetValuesByParam(form) {
    const selected = new Map();
    if (!form) return selected;

    const inputs = form.querySelectorAll('input[type="checkbox"][name^="filter."]:checked');
    inputs.forEach((input) => {
      const paramName = input.name;
      const normalized = normalizeFacetValue(input.value);
      if (!normalized) return;
      const existing = selected.get(paramName) || [];
      if (!existing.includes(normalized)) {
        existing.push(normalized);
      }
      selected.set(paramName, existing);
    });

    return selected;
  }

  function valueMatchesFacet(paramName, productFacetValues, selectedValues) {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
      return true;
    }

    if (String(paramName || '').toLowerCase().includes('country_of_origin')) {
      const expanded = expandCountryAliases(selectedValues);
      return expanded.some((value) => productFacetValues.includes(value));
    }

    return selectedValues.some((value) => productFacetValues.includes(value));
  }

  function productMatchesFacetSelections(productEntry, selectedByParam, excludeParamName) {
    for (const [paramName, selectedValues] of selectedByParam.entries()) {
      if (excludeParamName && paramName === excludeParamName) {
        continue;
      }
      if (!selectedValues || selectedValues.length === 0) {
        continue;
      }
      const values = getFacetValuesFromCatalogEntry(productEntry, paramName);
      if (!valueMatchesFacet(paramName, values, selectedValues)) {
        return false;
      }
    }
    return true;
  }

  function updateWineAssistantDependentFacets(form) {
    if (!form) return;

    const products = collectWineAssistantProductsForFacets();
    if (products.length === 0) return;

    const selectedByParam = getSelectedFacetValuesByParam(form);
    const groupedInputs = new Map();

    form.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
      if (!input.name) return;
      const arr = groupedInputs.get(input.name) || [];
      arr.push(input);
      groupedInputs.set(input.name, arr);
    });

    groupedInputs.forEach((inputs, paramName) => {
      inputs.forEach((input) => {
        const optionValue = normalizeFacetValue(input.value);
        if (!optionValue) return;

        if (input.checked) {
          input.disabled = false;
          const checkedLabel = form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          if (checkedLabel) checkedLabel.classList.remove('text-scheme-meta', 'line-through');
          return;
        }

        let hasMatch = false;
        for (const product of products) {
          if (!productMatchesFacetSelections(product.entry, selectedByParam, paramName)) {
            continue;
          }
          const facetValues = getFacetValuesFromCatalogEntry(product.entry, paramName);
          if (valueMatchesFacet(paramName, facetValues, [optionValue])) {
            hasMatch = true;
            break;
          }
        }

        input.disabled = !hasMatch;
        const label = form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (label) {
          label.classList.toggle('text-scheme-meta', !hasMatch);
          label.classList.toggle('line-through', !hasMatch);
        }
      });
    });
  }

  function setupWineAssistantDependentFacets(form) {
    if (!form || form.dataset.vinpopDependentFacetsBound === '1') {
      return;
    }

    form.dataset.vinpopDependentFacetsBound = '1';

    const runUpdate = () => {
      updateWineAssistantDependentFacets(form);
    };

    form.addEventListener('change', (event) => {
      const target = event.target;
      if (!target || !target.matches || !target.matches('input[type="checkbox"][name^="filter."]')) {
        return;
      }
      setTimeout(runUpdate, 0);
    });

    runUpdate();
  }

  function setWineAssistantCountryOrEmptyState(container, visibleCount) {
    if (!container) return;

    const isDutch = (document.documentElement.lang || '').toLowerCase().startsWith('nl');
    const emptyText = isDutch
      ? 'Sorry, er zijn geen producten in deze collectie'
      : 'Sorry, there are no products in this collection';

    const nativeEmpty = Array.from(container.querySelectorAll('p')).find((p) => {
      const txt = String(p.textContent || '').toLowerCase();
      return txt.includes('geen producten in deze collectie') || txt.includes('no products in this collection');
    });

    let customEmpty = container.querySelector('#vinpop-country-or-empty');
    if (!customEmpty) {
      customEmpty = document.createElement('p');
      customEmpty.id = 'vinpop-country-or-empty';
      customEmpty.style.display = 'none';
      customEmpty.textContent = emptyText;
      container.appendChild(customEmpty);
    }

    if (visibleCount === 0) {
      if (nativeEmpty) nativeEmpty.style.display = 'none';
      customEmpty.style.display = '';
    } else {
      if (nativeEmpty) nativeEmpty.style.display = 'none';
      customEmpty.style.display = 'none';
    }

    const resultsCount = document.getElementById('facets-results-count');
    if (resultsCount) {
      resultsCount.textContent = `${visibleCount} ${visibleCount === 1 ? 'product' : 'producten'}`;
    }
  }

  function applyWineAssistantCountryOrFilter(selectedCountries) {
    const normalizedSelected = expandCountryAliases(selectedCountries || []);

    const resultsContainer = document.getElementById('facets-results');
    if (!resultsContainer) return;

    const cards = Array.from(resultsContainer.querySelectorAll('li[data-product-id]'));
    if (cards.length === 0) return;

    if (normalizedSelected.length <= 1) {
      cards.forEach((card) => {
        card.style.removeProperty('display');
      });
      setWineAssistantCountryOrEmptyState(resultsContainer, cards.length);
      return;
    }

    let visibleCount = 0;
    cards.forEach((card) => {
      const productCountries = getCountryValuesForCard(card);
      const matches = normalizedSelected.some((country) => productCountries.includes(country));
      if (matches) {
        card.style.removeProperty('display');
        visibleCount += 1;
      } else {
        card.style.setProperty('display', 'none', 'important');
      }
    });

    setWineAssistantCountryOrEmptyState(resultsContainer, visibleCount);
  }

  function setupWineAssistantCountryOrFilter() {
    const pathname = window.location.pathname;
    if (!isWineAssistantCollectionPage(pathname)) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const hasSourceProduct = urlParams.get('source_product') || urlParams.get('main-product');
    if (hasSourceProduct) {
      return;
    }

    const nativeCountryValues = urlParams
      .getAll('filter.p.m.custom.country_of_origin')
      .flatMap((raw) => String(raw || '').split(','))
      .map((v) => v.trim())
      .filter(Boolean);

    if (!urlParams.get('vp_country_or') && nativeCountryValues.length > 1) {
      const nextParams = new URLSearchParams(window.location.search);
      nextParams.delete('filter.p.m.custom.country_of_origin');
      nextParams.set('vp_country_or', nativeCountryValues.join(','));
      window.location.replace(`${window.location.pathname}?${nextParams.toString()}`);
      return;
    }

    const urlCountryOr = String(urlParams.get('vp_country_or') || '').split(',').map((v) => v.trim()).filter(Boolean);

    const form = document.getElementById('Facet-Filters-Form');
    if (!form) {
      if (!window.__vinpopCountryOrSetupPending) {
        window.__vinpopCountryOrSetupPending = true;
        setTimeout(() => {
          window.__vinpopCountryOrSetupPending = false;
          setupWineAssistantCountryOrFilter();
        }, 150);
      }
      if (urlCountryOr.length > 1) {
        applyWineAssistantCountryOrFilter(urlCountryOr);
      }
      return;
    }

    if (form.dataset.vinpopCountryOrBound === '1') {
      if (urlCountryOr.length > 1) {
        applyWineAssistantCountryOrFilter(urlCountryOr);
      }
      return;
    }

    function getSelectedCountryValues() {
      return Array.from(
        form.querySelectorAll('input[type="checkbox"][name="filter.p.m.custom.country_of_origin"]:checked')
      ).map((input) => String(input.value || '').trim()).filter(Boolean);
    }

    function navigateWithCountryOr(selectedCountries) {
      const formData = new FormData(form);
      const nextParams = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        if (key === 'price_range' || value === '') {
          continue;
        }
        if (key === 'filter.p.m.custom.country_of_origin') {
          continue;
        }
        nextParams.append(key, value);
      }

      nextParams.set('vp_country_or', selectedCountries.join(','));
      window.location.assign(`${window.location.pathname}?${nextParams.toString()}`);
    }

    if (urlCountryOr.length > 1) {
      const normalizedUrlCountries = new Set(urlCountryOr.map(normalizeFacetValue));
      form.querySelectorAll('input[type="checkbox"][name="filter.p.m.custom.country_of_origin"]').forEach((input) => {
        input.checked = normalizedUrlCountries.has(normalizeFacetValue(input.value));
      });
      applyWineAssistantCountryOrFilter(urlCountryOr);
    }

    setupWineAssistantDependentFacets(form);

    form.dataset.vinpopCountryOrBound = '1';

    form.addEventListener('click', function (event) {
      const submitButton = event.target && event.target.closest
        ? event.target.closest('button[type="submit"], input[type="submit"]')
        : null;

      if (!submitButton || !form.contains(submitButton)) {
        return;
      }

      const selectedCountries = getSelectedCountryValues();
      if (selectedCountries.length <= 1) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      navigateWithCountryOr(selectedCountries);
    }, true);

    form.addEventListener('submit', function (event) {
      const selectedCountries = getSelectedCountryValues();

      if (selectedCountries.length <= 1) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      navigateWithCountryOr(selectedCountries);
    }, true);
  }
  
  // Expose globally for other scripts
  window.alignProductCards = alignProductCards;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupWineAssistantCountryOrFilter, { once: true });
  } else {
    setTimeout(setupWineAssistantCountryOrFilter, 0);
  }

  // Initial attachment on page load
  document.addEventListener('DOMContentLoaded', async function() {
    // Hide "log in to activate" message if customer is logged in
    const customerMeta = document.querySelector('meta[name="customer-id"]');
    if (customerMeta && customerMeta.content && customerMeta.content.trim() !== '') {
      document.querySelectorAll('.feature-text.inline-heading-rte').forEach(el => {
        if (el.textContent.includes('log in') || el.textContent.includes('inloggen') || el.textContent.includes('connecte')) {
          const section = el.closest('.shopify-section');
          if (section) section.style.display = 'none';
          else el.style.display = 'none';
        }
      });
    }

    // NO alignment on initial load - wait for food-pairing-filter.js to add badges first
    // This avoids the visible "jump" effect
    
    // Slight delay to allow Shopify's cookie banner to initialize first (only on homepage)
    const isHomepage = window.location.pathname === '/' || 
                      window.location.pathname.match(/^\/(en|nl|fr|de|es|it|pt|da)\/?$/);
    if (isHomepage) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    // Check if we have a source_product or main-product parameter
    const urlParams = new URLSearchParams(window.location.search);
    const hasSourceProduct = urlParams.get('source_product') || urlParams.get('main-product');

    // Shopify list-metafield facets behave as AND for multi-country selection.
    // Use a targeted local OR fallback only on /collections/wine-assistant.
    setupWineAssistantCountryOrFilter();

    // console.log('🔍 Current pathname:', window.location.pathname);
    // console.log('🔍 Has source_product:', hasSourceProduct);

    // Attach wine assistant navigation ONLY if we're not already in similar wines mode
    if (!hasSourceProduct) {
      attachWineAssistantNavigation();
    } else {
      // console.log('⏭️ Navigation interception skipped - already in similar wines mode');
    }

    // Re-attach after facets/sort reload (Shopify AJAX) - only if not in similar wines mode
    document.addEventListener('shopify:section:load', function() {
      const urlParams = new URLSearchParams(window.location.search);
      const hasSourceProduct = urlParams.get('source_product') || urlParams.get('main-product');
      
      if (!hasSourceProduct) {
        // console.log('🔄 Section reloaded, re-attaching navigation...');
        setTimeout(attachWineAssistantNavigation, 100);
      }
    });

    // Watch for DOM changes (filter updates) and re-attach navigation
    const productGrid = document.querySelector('ul[id*="product-grid"], .collection, .product-grid, [data-products]');
    if (productGrid) {
      const observer = new MutationObserver((mutations) => {
        const urlParams = new URLSearchParams(window.location.search);
        const hasSourceProduct = urlParams.get('source_product') || urlParams.get('main-product');
        
        if (!hasSourceProduct) {
          // console.log('🔄 DOM changed (filters applied), re-attaching navigation...');
          setTimeout(attachWineAssistantNavigation, 100);
          // Re-align cards after DOM changes
          setTimeout(alignProductCards, 350);
        }
      });
      
      observer.observe(productGrid, {
        childList: true,
        subtree: true
      });
      
      // console.log('👀 MutationObserver attached to product grid');
    }

    // Listen for custom event when food-pairing-filter reorganizes products
    document.addEventListener('vinpop:products-reordered', function() {
      const urlParams = new URLSearchParams(window.location.search);
      const hasSourceProduct = urlParams.get('source_product') || urlParams.get('main-product');
      
      if (!hasSourceProduct) {
        // console.log('🔄 Products reordered by food-pairing-filter, re-attaching navigation...');
        setTimeout(attachWineAssistantNavigation, 100);
        // Re-align cards after reorder
        setTimeout(alignProductCards, 350);
      }
    });

    // Check if we're on a special collection
    const pathname = window.location.pathname;
    const isWineAssistant = pathname.includes('/wine-assistant');
    const isAllWines = pathname.match(/\/collections\/(all|wine)$/);
    const isFoodPairing = pathname.includes('/food-pairing');
    // Disable quizz handling in collection-recommendations.js - let taste-profile-quiz.js handle it
    // const isQuizz = pathname.includes('/quizz') && urlParams.get('taste_profile') === 'true';
    const isSpecialCollection = isWineAssistant || isAllWines || isFoodPairing;

    // Exit early if not on special collection, or if on special collection without source_product
    if (!isSpecialCollection || (isSpecialCollection && !hasSourceProduct)) {
      // Still run alignment on wine-assistant page even without source_product
      if (isWineAssistant) {
        setTimeout(alignProductCards, 350);
      }
      return;
    }

    // Similar Wines Recommendations Logic (when source_product is in URL on special collections)
    if (hasSourceProduct) {
      // console.log('🍷 Loading similar wines recommendations...');

      // FIX: Ensure proper grid classes are applied (with collection-grid for responsive CSS)
      setTimeout(() => {
        const gridElement = document.querySelector('#facets-results ul');
        if (gridElement) {
          // Force correct responsive grid classes - 2 products per row on mobile, uses collection-grid-responsive.css
          gridElement.className = 'collection-grid grid gap-theme grid-cols-2 lg:grid-cols-5';
          // console.log('✅ Grid classes fixed for responsive layout');
        }
      }, 100);

      // Get source product ID from URL or sessionStorage
      let sourceProductId = urlParams.get('source_product') || urlParams.get('main-product');
      let sourceProductName = '';

      if (!sourceProductId) {
        sourceProductId = sessionStorage.getItem('wineAssistantClickedProduct');
        sourceProductName = sessionStorage.getItem('wineAssistantClickedProductName') || '';
        const storedTime = sessionStorage.getItem('wineAssistantClickedTime');

        if (sourceProductId && storedTime) {
          const timeDiff = Date.now() - parseInt(storedTime);
          if (timeDiff > 5 * 60 * 1000) { // 5 minutes expiry
            sourceProductId = null;
            sourceProductName = '';
          }
        }
      }

      if (!sourceProductId) {
        // console.log('❌ No source product specified');
        return;
      }

      // console.log('✅ Source product ID:', sourceProductId);

      // Fetch source product name from Shopify API
      try {
        // First try to find the product in the current collection
        const currentProducts = Array.from(document.querySelectorAll('li[data-product-id]'));
        const sourceProductElement = currentProducts.find(el => el.getAttribute('data-product-id') === sourceProductId);

        if (sourceProductElement) {
          const titleElement = sourceProductElement.querySelector('h3, h2, .product-title, [class*="title"]');
          if (titleElement) {
            sourceProductName = titleElement.textContent.trim();
            // console.log('📦 Source product name from DOM:', sourceProductName);
          }
        }

        // If we didn't get the name from DOM, try the Shopify product API
        if (!sourceProductName) {
          // console.log('🔍 Trying to fetch product via handle...');
          // Try to get product handle from URL parameters or session storage
          const urlParams = new URLSearchParams(window.location.search);
          const productHandle = urlParams.get('handle');

          if (productHandle) {
            const productResponse = await fetch(`/products/${productHandle}.js`);
            if (productResponse.ok) {
              const productData = await productResponse.json();
              sourceProductName = productData.title;
              // console.log('📦 Source product name from API:', sourceProductName);
            }
          }
        }
      } catch (error) {
        // console.log('⚠️ Could not fetch product name:', error);
      }

      try {
        // Get compatibility data from API
        const response = await fetch('https://api.vinpop.fr/api/wines/getDistance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: sourceProductId })
        });

        const rawCompatibilityData = await response.json();
        const compatibilityData = Array.isArray(rawCompatibilityData)
          ? rawCompatibilityData
          : (Array.isArray(rawCompatibilityData?.value) ? rawCompatibilityData.value : []);
        // console.log('📊 Compatibility data received:', compatibilityData);

        if (!compatibilityData || compatibilityData.length === 0) {
          // console.log('❌ No compatibility data found');
          return;
        }

        // ========== NEW APPROACH: Use WineRecommendationManager ==========
        const wineManager = await waitForWineManager();
        if (wineManager) {
          // console.log('🍷 Using WineRecommendationManager for rendering...');
          
          // Transform API data to WineRecommendationManager format
          const formattedWines = [];
          const processedIds = new Set();

          compatibilityData.forEach(wine => {
            // Extract both product IDs from the compatibility pair
            [wine.productid1, wine.productid2].forEach(productId => {
              productId = String(productId);
              // Skip source product and duplicates
              if (productId === String(sourceProductId) || processedIds.has(productId)) return;
              processedIds.add(productId);

              // Calculate compatibility score
              const distance = wine.perceptive_distance || wine.distance;
              const score = Math.max(0, Math.floor(100 - distance));

              formattedWines.push({
                wine_id: productId,
                score: score,
                distance: distance
              });
            });
          });

          // Add the selected source product at the top (100% match)
          formattedWines.unshift({
            wine_id: sourceProductId,
            score: 100,
            distance: 0,
            recommendation_text: 'Selected wine' // No checkmark for cleaner badge display
          });

          // console.log('✅ Formatted wines for Manager:', formattedWines.length, 'products');

          // Delegate to WineRecommendationManager (handles badges, filters, drawer)
          await wineManager.renderAssistantResults(formattedWines);
          
        } else {
          console.error('❌ WineRecommendationManager not loaded - falling back to manual rendering');
          
          // FALLBACK: Old manual DOM manipulation (kept for safety)
          const productCompatibilityMap = {};
          compatibilityData.forEach(wine => {
            [wine.productid1, wine.productid2].forEach(productId => {
              productId = String(productId);
              if (productId === String(sourceProductId)) return;

              const distance = wine.perceptive_distance || wine.distance;
              const compatibility = Math.max(0, Math.floor(100 - distance));

              if (!productCompatibilityMap[productId] || compatibility > productCompatibilityMap[productId]) {
                productCompatibilityMap[productId] = compatibility;
              }
            });
          });

          const allProducts = Array.from(document.querySelectorAll('li[data-product-id]'));
          const productsWithCompatibility = [];
          let selectedProduct = null;
          let selectedProductName = sourceProductName;

          allProducts.forEach(productElement => {
            const productId = productElement.getAttribute('data-product-id');
            if (!productId) return;

            if (productId === String(sourceProductId)) {
              if (!selectedProductName) {
                const titleElement = productElement.querySelector('h3, h2, .product-title, [class*="title"]');
                if (titleElement) {
                  selectedProductName = titleElement.textContent.trim();
                }
              }
              addSimilarWinesRecommendation(productElement, 100, selectedProductName, true);
              selectedProduct = { element: productElement, compatibility: 100 };
              productElement.style.display = '';
            } else {
              const compatibility = productCompatibilityMap[productId];

              if (compatibility !== undefined) {
                addSimilarWinesRecommendation(productElement, compatibility, selectedProductName || 'selected wine');
                productsWithCompatibility.push({ element: productElement, compatibility });
                productElement.style.display = '';
              } else {
                productElement.style.display = 'none';
              }
            }
          });

          productsWithCompatibility.sort((a, b) => b.compatibility - a.compatibility);

          const productGrid = document.querySelector('#facets-results ul') ||
                            document.querySelector('#product-grid') ||
                            document.querySelector('.collection-products') ||
                            document.querySelector('.grid');

          if (productGrid) {
            if (selectedProduct) {
              productGrid.appendChild(selectedProduct.element);
            }

            if (productsWithCompatibility.length > 0) {
              productsWithCompatibility.forEach(item => {
                productGrid.appendChild(item.element);
              });
            }

            const totalShown = (selectedProduct ? 1 : 0) + productsWithCompatibility.length;
            // console.log(`✅ ${totalShown} products sorted (selected first, then by compatibility). Hidden: ${allProducts.length - totalShown}`);
          }
        }

      } catch (error) {
        console.error('❌ Error loading similar wines recommendations:', error);
      }

      return; // Exit early for similar-wines collection
    }

    // Only run on collection/all page
    if (!window.location.pathname.includes('/collections/all')) {
      return;
    }

    // console.log('🍷 Loading wine recommendations for collection... [FIXED]');

    // Get customer ID
    const getCustomerId = () => {
      const metaTag = document.querySelector('meta[name="customer-id"]');
      return metaTag ? metaTag.getAttribute('content') : null;
    };

    const customerId = getCustomerId();
    if (!customerId) {
      // console.log('No customer logged in - displaying original products without modifications');
      return; // Exit early, leave original products as they are
    }

    const getMetaContent = (name) => {
      const meta = document.querySelector(`meta[name="${name}"]`);
      return meta ? String(meta.getAttribute('content') || '').trim() : '';
    };

    const buildVinpopAuthHeaders = () => {
      const timestamp = getMetaContent('friends-auth-timestamp');
      const signature = getMetaContent('friends-auth-signature');
      if (!customerId || !timestamp || !signature) {
        throw new Error('vinpop_auth_unavailable');
      }

      return {
        'Content-Type': 'application/json',
        'X-Customer-Id': String(customerId),
        'X-Timestamp': timestamp,
        'X-Signature': signature
      };
    };

    // console.log('✅ Customer ID found:', customerId);

    try {
      // Get loved products from PostgreSQL
      const response = await fetch(`https://api.vinpop.fr/api/ratings/getAll?customerId=${customerId}`, {
        headers: buildVinpopAuthHeaders()
      });
      const ratingsArray = await response.json();

      if (!ratingsArray || ratingsArray.length === 0) {
        // console.log('No ratings found - displaying original products without modifications');
        return; // Exit early, leave original products as they are
      }

      // Filter for loved products (rating = "3")
      const lovedProductIds = ratingsArray
        .filter(item => String(item.rating) === "3")
        .map(item => item.id);


      if (lovedProductIds.length === 0) {
        // console.log('No loved products - displaying original products without modifications');
        return; // Exit early, leave original products as they are
      }

      // Get all API responses for loved products
      let allApiResponses = [];
      let completedCalls = 0;

      // Get names of loved products - use correct selector with data-product-id
      const lovedProductNames = {};
      lovedProductIds.forEach(productId => {
        const productElement = document.querySelector(`li[data-product-id="${productId}"]`);
        if (productElement) {
          // Try multiple selectors for Shapes theme structure
          const productLink = productElement.querySelector('h3, .card__heading a, h3 a, .product-tile h3');
          if (productLink) {
            lovedProductNames[productId] = productLink.textContent.trim();
          }
        }
      });

      // Function to process all API responses and calculate compatibility
      function processAllRecommendations() {
        if (completedCalls !== (lovedProductIds || []).length) return;

        // Create a map to store highest compatibility and source product for each product
        const productCompatibilityMap = {};

        // Process all API responses
        allApiResponses.forEach((responseData, index) => {
          const { response, sourceProductId } = responseData;
          if (!response || !response.length) return;

          response.forEach(wine => {
            // Check both productid1 and productid2 as potential matches
            [wine.productid1, wine.productid2].forEach(productId => {
              // Skip if this is the loved product itself
              if ((lovedProductIds || []).includes(productId)) return;

              const distance = wine.perceptive_distance || wine.distance;
              const compatibility = Math.max(0, Math.floor(100 - distance));

              // Keep highest compatibility and track source product
              if (!productCompatibilityMap[productId] || compatibility > productCompatibilityMap[productId].compatibility) {
                productCompatibilityMap[productId] = {
                  compatibility: compatibility,
                  sourceProductId: sourceProductId,
                  sourceProductName: lovedProductNames[sourceProductId] || ''
                };
              }
            });
          });
        });

        // Get all products and create array for sorting
        const allProducts = Array.from(document.querySelectorAll('#product-grid li[data-product-id], #facets-results ul > li[data-product-id], .collection ul > li[data-product-id]'));

        const productsWithCompatibility = [];

        allProducts.forEach(productElement => {
          const productId = productElement.getAttribute('data-product-id');
          if (!productId) return;

          let compatibility = 0;
          let message = '';
          let isLoved = false;

          // Check if this is a loved product
          if ((lovedProductIds || []).includes(productId)) {
            compatibility = 100; // Highest priority for sorting
            message = getTranslation('you_loved_it');
            isLoved = true;
          } else if (productCompatibilityMap[productId] !== undefined) {
            const productData = productCompatibilityMap[productId];
            compatibility = productData.compatibility;
            const sourceProductName = productData.sourceProductName;

            // Use exact same messages as mrk.js with translations
            if (compatibility >= 95) {
              message = `${compatibility}% ${getTranslation('professional_tasters')}`;
            } else if (compatibility >= 91) {
              message = `${compatibility}% ${getTranslation('wine_connoisseurs')}`;
            } else if (compatibility >= 85) {
              message = `${compatibility}% ${getTranslation('very_close_profile')}`;
            } else if (compatibility >= 80) {
              message = `${compatibility}% ${getTranslation('related_wine')}`;
            } else {
              message = `${compatibility}% ${getTranslation('different_taste')}`;
            }

            // Add source product name if available
            if (sourceProductName) {
              // Don't truncate - let the text wrap naturally
              message += `, ${getTranslation('because_you_loved')} ${sourceProductName}`;
            }
          }

          if (message) {
            productsWithCompatibility.push({
              element: productElement,
              compatibility,
              message,
              isLoved
            });
          }
        });

        // Sort products by compatibility descending (like similar-wines)
        productsWithCompatibility.sort((a, b) => b.compatibility - a.compatibility);

        // Get the product grid and reorder products
        const productGrid = document.querySelector('#product-grid, #facets-results ul, .collection ul');
        if (productGrid) {
          // Remove all products from grid
          const allProductElements = Array.from(document.querySelectorAll('#product-grid li[data-product-id], #facets-results ul > li[data-product-id], .collection ul > li[data-product-id]'));
          allProductElements.forEach(product => product.remove());

          // Add products back in sorted order (recommendations first)
          productsWithCompatibility.forEach(item => {
            productGrid.appendChild(item.element);
            addRecommendationDiv(item.element, item.message, item.isLoved);
          });

          // Add products without recommendations at the end
          allProductElements.forEach(productElement => {
            const productId = productElement.getAttribute('data-product-id');
            const hasRecommendation = productsWithCompatibility.some(item =>
              item.element.getAttribute('data-product-id') === productId
            );

            if (!hasRecommendation) {
              productGrid.appendChild(productElement);
            }
          });
        }

        // console.log(`✅ Applied ${productsWithCompatibility.length} wine recommendations`);
      }

      // Function to add recommendation div under h3
      function addRecommendationDiv(productElement, message, isLoved) {
        // Look for the product-tile div which contains the h3
        const productTile = productElement.querySelector('.product-tile');

        if (!productTile) return;

        // Find the h3 element
        const h3Element = productTile.querySelector('h3');
        if (!h3Element) return;

        // Remove existing recommendation if any
        const existingRec = productTile.querySelector('.wine-recommendation');
        if (existingRec) {
          existingRec.remove();
        }

        // Use same simple style as similar-wines for consistency
        if (message) {
          const recommendationDiv = document.createElement('div');
          recommendationDiv.className = 'wine-recommendation';
          recommendationDiv.style.cssText = 'color:#666;font-style:italic;font-size:12px;margin-top:4px;';
          recommendationDiv.textContent = message;
          h3Element.insertAdjacentElement('afterend', recommendationDiv);
        }
      }

      // Make API calls for each loved product - same as homepage-recommendations.js
      lovedProductIds.forEach(lovedProductId => {
        const settings = {
          url: "https://api.vinpop.fr/api/wines/getDistance",
          method: "POST",
          cache: true,
          headers: {
            "Content-Type": "application/json",
          },
          data: JSON.stringify({
            id: String(lovedProductId)
          }),
        };

        $.ajax(settings)
          .done(function (response) {
            if (!response || !response.length) {
              completedCalls++;
              processAllRecommendations();
              return;
            }

            allApiResponses.push({ response: response, sourceProductId: lovedProductId });
            completedCalls++;
            processAllRecommendations();
          })
          .fail(function (error) {
            completedCalls++;
            processAllRecommendations();
          });
      });

      // FILTER DETECTION: Watch for DOM changes when filters are applied
      let filterObserverActive = false;
      let reapplyTimeout = null;
      let lastProductCount = 0;

      function setupFilterObserver() {
        if (filterObserverActive) return;

        const facetsResults = document.querySelector('#facets-results');
        if (!facetsResults) {
          // console.log('⚠️ #facets-results not found, cannot setup filter observer');
          return;
        }

        // Count initial products
        lastProductCount = document.querySelectorAll('#facets-results ul > li[data-product-id]').length;
        // console.log('📊 Initial product count:', lastProductCount);

        // METHOD 1: MutationObserver - IMPROVED to detect product changes
        const observer = new MutationObserver((mutations) => {
          // Count current products
          const currentProductCount = document.querySelectorAll('#facets-results ul > li[data-product-id]').length;
          
          // Check if product count changed (filter was applied)
          if (currentProductCount !== lastProductCount && currentProductCount > 0) {
            // console.log(`🔄 [MutationObserver] Product count changed: ${lastProductCount} → ${currentProductCount}`);
            lastProductCount = currentProductCount;
            
            // Debounce: wait 600ms to ensure DOM is fully updated
            clearTimeout(reapplyTimeout);
            reapplyTimeout = setTimeout(() => {
              // console.log('🔄 Reapplying recommendations after filter...');
              processAllRecommendations();
            }, 600);
          }
        });

        observer.observe(facetsResults, {
          childList: true,
          subtree: true,
          attributes: false
        });

        // METHOD 2: Watch for URL changes (Shopify updates URL when filter applied)
        let lastUrl = window.location.href;
        setInterval(() => {
          const currentUrl = window.location.href;
          if (currentUrl !== lastUrl && currentUrl.includes('filter.')) {
            // console.log('🔄 [URL Monitor] Filter detected in URL');
            // console.log('   Current URL:', currentUrl);
            // console.log('   Previous URL:', lastUrl);
            lastUrl = currentUrl;
            
            // Update lastProductCount BEFORE triggering reapply
            const newProductCount = document.querySelectorAll('#facets-results ul > li[data-product-id]').length;
            // console.log(`   Waiting for product count to update from ${lastProductCount}...`);
            
            clearTimeout(reapplyTimeout);
            reapplyTimeout = setTimeout(() => {
              // Wait extra time for Shopify to fully render filtered products
              const finalProductCount = document.querySelectorAll('#facets-results ul > li[data-product-id]').length;
              // console.log(`🔄 Reapplying recommendations (products: ${lastProductCount} → ${finalProductCount})...`);
              lastProductCount = finalProductCount;
              processAllRecommendations();
            }, 1500); // Increased delay to 1.5s to ensure DOM is updated
          }
        }, 500);

        // METHOD 3: Watch for Alpine.js events (Shapes theme uses Alpine)
        document.addEventListener('alpine:init', () => {
          // console.log('� Alpine.js detected, listening for filter events...');
        });

        filterObserverActive = true;
        // console.log('✅ Filter observer setup complete (MutationObserver + URL Monitor)');
      }

      // Setup observer after initial recommendations are applied
      setTimeout(() => {
        setupFilterObserver();
      }, 500);

    } catch (error) {
      console.error('Error loading wine recommendations:', error);
    }
  });

  // Helper function for similar wines recommendations
  function addSimilarWinesRecommendation(productElement, compatibility, selectedProductName, isSelectedProduct = false) {
    // Try multiple selectors for better compatibility with different themes
    let targetDiv = productElement.querySelector('.card__information') ||
                   productElement.querySelector('.product-tile__info') ||
                   productElement.querySelector('.product-card__info') ||
                   productElement.querySelector('[class*="product"]');

    if (!targetDiv) {
      // console.log('⚠️ No target div found for product', productElement);
      return;
    }

    // Find the title element (h3, h2, or any heading with product title)
    const h3InTargetDiv = targetDiv.querySelector('h3, h2, .product-title, [class*="title"]');
    if (!h3InTargetDiv) {
      // console.log('⚠️ No title element found in', targetDiv);
      return;
    }

    // Remove existing recommendation
    const existingRec = targetDiv.querySelector('.product-compatibility-display');
    if (existingRec) {
      existingRec.remove();
    }

    // Create new recommendation div with simple styling to match collections/all exactly
    const recommendationDiv = document.createElement('div');
    recommendationDiv.className = 'product-compatibility-display';
    recommendationDiv.style.cssText = 'color: #666; font-style: italic; font-size: 14px; margin-top: 8px; line-height: 1.4;';
    
    // console.log('📝 Creating recommendation div for product', productElement.getAttribute('data-product-id'), 'compatibility:', compatibility);

    if (compatibility !== null && compatibility !== undefined) {
      // Don't use span - put text directly in the div like collections/all
      // const span = document.createElement('span');
      // span.className = 'wine-compatibility-text';

      let message = '';
      let useAccentColor = false;

      if (isSelectedProduct) {
        message = `✓ ${getTranslation('selected_wine')}`;
        useAccentColor = true;
      } else if (compatibility >= 95) {
        message = `${compatibility}% ${getTranslation('professional_tasters')}`;
        useAccentColor = true;
      } else if (compatibility >= 91) {
        message = `${compatibility}% ${getTranslation('wine_connoisseurs')}`;
        useAccentColor = true;
      } else if (compatibility >= 85) {
        message = `${compatibility}% ${getTranslation('very_close_profile')}`;
      } else if (compatibility >= 80) {
        message = `${compatibility}% ${getTranslation('related_wine')}`;
      } else {
        message = `${compatibility}% ${getTranslation('different_taste')}`;
      }

      if (!isSelectedProduct && selectedProductName) {
        message += `, ${getTranslation('similar_to')} ${selectedProductName}`;
      }

      // No custom styling - put text directly in div like collections/all
      recommendationDiv.textContent = message;
      // console.log('✅ Recommendation message:', message);
    }

    h3InTargetDiv.insertAdjacentElement('afterend', recommendationDiv);
    addSimilarWinesBadge(productElement, compatibility, isSelectedProduct);
    // console.log('✅ Recommendation div inserted after title');
  }

  function addSimilarWinesBadge(productElement, compatibility, isSelectedProduct = false) {
    productElement.querySelectorAll('.vinpop-score-badge').forEach(badge => badge.remove());

    const mediaLink = productElement.querySelector('a.product-tile__featured-media, a[href*="/products/"]');
    if (!mediaLink || compatibility === null || compatibility === undefined) return;

    mediaLink.style.position = 'relative';

    const isNL = (document.documentElement.lang || '').toLowerCase().startsWith('nl');
    const badgeContent = isSelectedProduct
      ? `<span style="font-size: var(--font-size-base); font-weight: 700; line-height: 1.1; text-align: center;">${isNL ? 'Gekozen' : 'Selected'}</span>`
      : `<div style="line-height:1.1;"><span style="font-size: var(--font-size-base);">${compatibility}%</span><br><span style="font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.03em;">match</span></div>`;

    mediaLink.insertAdjacentHTML('beforeend', `
      <div class="vinpop-score-badge pointer-events-none absolute z-20 origin-top-left will-change-transform"
           style="top: 15%; right: 10%; transform: rotate(5deg) translate(50%, -50%);">
        <div class="sticker__container rotate-anim relative">
          <div class="relative max-w-none">
            <div class="absolute inset-0" style="clip-path: circle(50%); background: rgb(var(--color-scheme-accent-2)); color: rgb(var(--color-scheme-accent-2-contrast));">
              <div class="sticker__text absolute inset-0 flex items-center justify-center text-center font-heading" style="font-family: var(--heading-font-stack); font-weight: var(--heading-font-weight); text-transform: var(--sticker-text-transform); font-size: var(--font-size-base); line-height: var(--base-line-height);">
                ${badgeContent}
              </div>
            </div>
            <svg class="sticker__width-container relative max-w-none text-scheme-text-contrast" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style="width: 80px; height: 80px;">
              <circle class="sticker__svg fill-transparent" cx="50" cy="50" r="48" stroke="rgb(var(--color-scheme-text))" stroke-width="0"></circle>
            </svg>
          </div>
        </div>
      </div>
    `);
  }
}
